import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';
import { MapPin, Building, User, Stethoscope } from 'lucide-react';
import ReactDOMServer from 'react-dom/server';

// Fix for default marker icons in Leaflet with Next.js
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

/**
 * MapView Component with Custom Icon System
 * 
 * Supports three location types with custom Lucide icons:
 * - 'provider': Building icon (blue)
 * - 'subscriber': User icon (green) 
 * - 'patient': Stethoscope icon (pink)
 * 
 * Location data should include: { lat, lng, name, city, state, type }
 * 
 * Example usage:
 * const sampleData = [
 *   { lat: 37.7749, lng: -122.4194, name: "Provider 1", city: "San Francisco", state: "CA", type: "provider" },
 *   { lat: 37.7849, lng: -122.4094, name: "Subscriber 1", city: "San Francisco", state: "CA", type: "subscriber" },
 *   { lat: 37.7949, lng: -122.3994, name: "Patient 1", city: "San Francisco", state: "CA", type: "patient" }
 * ];
 * 
 * <MapView locationData={sampleData} loading={false} />
 */

// Helper function to render Lucide React component to SVG string
const renderLucideSvg = (IconComponent, size = 16, color = 'white') => {
  const iconElement = React.createElement(IconComponent, { size, color });
  return ReactDOMServer.renderToStaticMarkup(iconElement);
};

// Helper function to create custom icons with Lucide icons
const createCustomIcon = (type) => {
  let IconComponent;
  let bgColor;
  let borderColor;
  
  switch (type) {
    case 'provider':
      IconComponent = Building;
      bgColor = '#87cefa';
      borderColor = '#2791d2';
      break;
    case 'subscriber':
      IconComponent = User;
      bgColor = '#90EE90';
      borderColor = '#32CD32';
      break;
    case 'patient':
      IconComponent = User;
      bgColor = '#FFB6C1';
      borderColor = '#FF69B4';
      break;
    default:
      if (type) {
        console.warn(`Unknown location type: "${type}". Using default marker.`);
      }
      IconComponent = MapPin;
      bgColor = '#D3D3D3';
      borderColor = '#808080';
  }

  // Render the Lucide component to SVG string
  const svgString = renderLucideSvg(IconComponent, 16, 'white');

  const iconHtml = `
    <div style="
      background-color: ${bgColor};
      border: 2px solid ${borderColor};
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    ">
      ${svgString}
    </div>
  `;

  return L.divIcon({
    html: iconHtml,
    className: 'custom-marker',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const MapView = ({ locationData = [], loading }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const isUnmountingRef = useRef(false);
  const fadeOverlayRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const { theme } = useTheme();

  // Function to show fade overlay
  const showFadeOverlay = () => {
    if (fadeOverlayRef.current) {
      fadeOverlayRef.current.style.opacity = '1';
      fadeOverlayRef.current.style.pointerEvents = 'auto';
    }
  };

  // Function to hide fade overlay
  const hideFadeOverlay = () => {
    if (fadeOverlayRef.current) {
      fadeOverlayRef.current.style.opacity = '0';
      fadeOverlayRef.current.style.pointerEvents = 'none';
    }
  };

  useEffect(() => {
    // Set unmounting flag on cleanup
    return () => {
      isUnmountingRef.current = true;
    };
  }, []);

  // Show fade overlay when loading
  useEffect(() => {
    if (loading) {
      showFadeOverlay();
    } else {
      // Small delay to ensure data is processed before hiding
      setTimeout(() => {
        hideFadeOverlay();
      }, 200);
    }
  }, [loading]);

  // Handle locationData changes with fade sequence
  useEffect(() => {
    if (mapInstanceRef.current && locationData.length > 0 && !loading && hasInitializedRef.current) {
      // Only run fade sequence for subsequent data changes (not initial load)
      // Step 1: Fade out
      showFadeOverlay();
      
      // Step 2: Wait for fade out, then update markers
      setTimeout(() => {
        if (mapInstanceRef.current && !isUnmountingRef.current) {
          try {
            // Clear existing markers
            mapInstanceRef.current.eachLayer((layer) => {
              if (layer instanceof L.Marker) {
                mapInstanceRef.current.removeLayer(layer);
              }
            });
            
            // Create a group to hold all markers for bounds calculation
            const markerGroup = L.featureGroup();
            
            // Add new markers
            locationData.forEach(location => {
              try {
                const customIcon = createCustomIcon(location.type);
                const marker = L.marker([location.lat, location.lng], { icon: customIcon })
                  .bindPopup(`
                    <div style="font-family: system-ui, -apple-system, sans-serif;">
                      <strong>${location.name}<br>${location.city}, ${location.state}</strong>
                      <br><small style="color: var(--brand-blue);">${location.type}</small>
                    </div>
                  `);
                
                if (mapInstanceRef.current && mapInstanceRef.current.hasLayer) {
                  marker.addTo(mapInstanceRef.current);
                  markerGroup.addLayer(marker);
                }
              } catch (markerError) {
                console.warn('Error creating marker:', markerError);
              }
            });
            
            // Step 3: Fit bounds
            if (markerGroup.getLayers().length > 0) {
              const bounds = markerGroup.getBounds();
              if (bounds.isValid()) {
                mapInstanceRef.current.fitBounds(bounds, {
                  padding: [77,77],
                  maxZoom: 12,
                  animate: false // No animation since we're fading
                });
              }
            }
            
            // Step 4: Fade back in after a short delay
            setTimeout(() => {
              hideFadeOverlay();
            }, 300);
            
          } catch (error) {
            console.warn('Error updating markers:', error);
            hideFadeOverlay(); // Hide overlay on error
          }
        }
      }, 500); // Wait for fade out to complete
    }
  }, [locationData, loading]);

  useEffect(() => {
    if (!mapInstanceRef.current) {
      // Ensure the map container exists before initializing
      if (!mapRef.current || isUnmountingRef.current) {
        console.warn('Map container not ready or component unmounting');
        return;
      }

      try {
        // Initialize map with more granular zoom control
        mapInstanceRef.current = L.map(mapRef.current, {
          center: [39.8283, -98.5795], // Center of USA
          zoom: 4,
          minZoom: 3,
          maxZoom: 18,
          zoomControl: false, // Disable default zoom control
          scrollWheelZoom: true,
          doubleClickZoom: true,
          touchZoom: true,
          boxZoom: true,
          keyboard: true,
          dragging: true,
          zoomSnap: 0.25, // Allow half-step zoom levels
          zoomDelta: 0.25, // Zoom by half steps
        });

        // Add tile layer with theme-aware style
        const tileUrl = theme === 'dark'
          ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
          : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

        const tileLayer = L.tileLayer(tileUrl, {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: 'abcd',
          maxZoom: 18
        }).addTo(mapInstanceRef.current);

        // Add custom zoom control
        L.control.zoom({
          position: 'topright',
          zoomInText: '+',
          zoomOutText: '−'
        }).addTo(mapInstanceRef.current);

        // Add custom legend
        const legend = L.control({ position: 'bottomleft' });
        legend.onAdd = function() {
          const div = L.DomUtil.create('div', 'info legend');
          div.style.backgroundColor = theme === 'dark' ? '#2a2a2a' : '#ffffff';
          div.style.padding = '10px';
          div.style.borderRadius = '8px';
          div.style.border = `1px solid ${theme === 'dark' ? '#404040' : '#e0e0e0'}`;
          div.style.color = theme === 'dark' ? '#ffffff' : '#000000';
          div.style.fontSize = '12px';
          div.style.fontFamily = 'system-ui, -apple-system, sans-serif';
          
          const types = [
            { type: 'provider', label: 'Provider', icon: '🏢' },
            { type: 'subscriber', label: 'Subscriber', icon: '👤' },
            { type: 'patient', label: 'Patient', icon: '🏥' }
          ];
          
          // div.innerHTML = '<strong>Location Types</strong><br>';
          div.innerHTML = '';
          types.forEach(({ type, label, icon }) => {
            try {
              const iconHtml = createCustomIcon(type).options.html;
              div.innerHTML += `
                <div style="display: flex; align-items: center; margin: 4px 0;">
                  <div style="margin-right: 8px;">${iconHtml}</div>
                  <span>${label}</span>
                </div>
              `;
            } catch (error) {
              console.warn('Error creating legend icon:', error);
            }
          });
          
          return div;
        };
        
        // Only add legend if map is still valid
        if (mapInstanceRef.current && !mapInstanceRef.current._removed) {
          try {
            legend.addTo(mapInstanceRef.current);
          } catch (error) {
            console.warn('Error adding legend:', error);
          }
        }

        // Wait for map to be fully loaded before adding markers and fitting bounds
        mapInstanceRef.current.whenReady(() => {
          // Add initial markers if data is available
          if (!loading && locationData.length > 0 && mapInstanceRef.current && !isUnmountingRef.current) {
            try {
              hasInitializedRef.current = true;
              
              // Create a group to hold all markers for bounds calculation
              const markerGroup = L.featureGroup();
              
              locationData.forEach(location => {
                try {
                  const customIcon = createCustomIcon(location.type);
                  const marker = L.marker([location.lat, location.lng], { icon: customIcon })
                    .bindPopup(`
                      <div style="font-family: system-ui, -apple-system, sans-serif;">
                        <strong>${location.name}<br>${location.city}, ${location.state}</strong>
                        <br><small style="color: var(--brand-blue);">${location.type}</small>
                      </div>
                    `);
                  
                  if (mapInstanceRef.current && mapInstanceRef.current.hasLayer) {
                    marker.addTo(mapInstanceRef.current);
                    markerGroup.addLayer(marker);
                  }
                } catch (markerError) {
                  console.warn('Error creating marker:', markerError);
                }
              });
              
              // Fit bounds for initial load
              if (markerGroup.getLayers().length > 0) {
                const bounds = markerGroup.getBounds();
                if (bounds.isValid()) {
                  mapInstanceRef.current.fitBounds(bounds, {
                    padding: [77,77],
                    maxZoom: 12,
                    animate: true
                  });
                }
              }
            } catch (error) {
              console.warn('Error adding initial markers:', error);
            }
          } else {
                      }
        });
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    }

    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        try {
          // Mark map as being removed to prevent new operations
          mapInstanceRef.current._removed = true;
          
          // Remove all layers first
          if (mapInstanceRef.current.eachLayer) {
            mapInstanceRef.current.eachLayer((layer) => {
              try {
                mapInstanceRef.current.removeLayer(layer);
              } catch (e) {
                // Ignore errors during cleanup
              }
            });
          }
          
          // Remove the map
          mapInstanceRef.current.remove();
        } catch (error) {
          console.warn('Error removing map:', error);
        } finally {
          mapInstanceRef.current = null;
        }
      }
    };
  }, [theme]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '300px',
        borderRadius: 1,
        overflow: 'hidden',
        background: 'var(--bg-panel)',
        position: 'relative', // Add relative positioning for absolute overlay
        '& .leaflet-container': {
          width: '100%',
          height: '100%',
          background: 'var(--bg-panel)',
        },
        '& .leaflet-control-zoom': {
          border: 'none',
          '& a': {
            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
            color: theme === 'dark' ? '#ffffff' : '#000000',
            border: `1px solid ${theme === 'dark' ? '#404040' : '#e0e0e0'}`,
            '&:hover': {
              backgroundColor: theme === 'dark' ? '#404040' : '#f5f5f5',
            },
          },
        },
        '& .leaflet-popup-content-wrapper': {
          backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
          color: theme === 'dark' ? '#ffffff' : '#000000',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
        '& .leaflet-popup-tip': {
          backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
        },
        '& .custom-marker': {
          background: 'transparent',
          border: 'none',
          '&:hover': {
            transform: 'scale(1.1)',
            transition: 'transform 0.2s ease',
          },
        },
        '& .legend': {
          '& .custom-marker': {
            transform: 'scale(0.8)',
            '&:hover': {
              transform: 'scale(0.8)',
            },
          },
        },
      }}
    >
      <div ref={mapRef} />
      <div
        ref={fadeOverlayRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          opacity: '0',
          pointerEvents: 'none',
          transition: 'opacity 0.5s ease-in-out',
          borderRadius: '4px', // Match the Box borderRadius
        }}
      />
    </Box>
  );
};

export default MapView; 